import { Injectable } from '@angular/core';
import { RecordModel } from 'pocketbase';
import {
  BookmarkedQuestion,
  Evaluation,
  FlaggedQuestion,
  Question,
  QuestionList,
  Response,
} from '../interfaces/questionInterface';
import { PocketbaseService } from './pocketbase.service';
import { Chance } from 'chance';

@Injectable({
  providedIn: 'root',
})
export class QuestionService {
  constructor(private readonly pocketbaseService: PocketbaseService) { }

  async getOneQuestion(questionId: string): Promise<Question> {
    const record = await this.pocketbaseService.pb
      .collection('questions')
      .getOne<Question>(questionId, {
        expand: 'answer_options',
      });

    return record;
  }

  async getEndlessQuestion(courseId: string): Promise<Question> {
    const record = await this.pocketbaseService.pb.send<Question>(
      `/endless/${courseId}`,
      { method: 'GET' }
    );

    return record;
  }

  async getIncorrectQuestion(): Promise<Question | undefined> {
    const record = await this.pocketbaseService.pb.send<Question>(
      `/endless/incorrect`,
      { method: 'GET' }
    );

    return record;
  }

  async getIncorrectQuestions(): Promise<Question[]> {
    const records = await this.pocketbaseService.pb.send<Question[]>(
      `/endless/incorrects`,
      { method: 'GET' }
    );
    return records;
  }

  async getIncorrectQuestionCount(): Promise<number> {
    const count = await this.pocketbaseService.pb.send<number>(
      `/endless/incorrect/total`,
      { method: 'GET' }
    );

    return count;
  }

  async submitTestAnswer(
    testId: string,
    questionId: string,
    answerIds: string[],
    answerTime: number,
    orderSeed: string
  ) {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      return await this.pocketbaseService.pb.send(`/response/${userId}`, {
        body: {
          question: questionId,
          given_answers: answerIds,
          test: testId,
          answer_time: answerTime,
          order_seed: orderSeed
        } as Response,
        method: 'POST',
      });
    }
    throw new Error('Not logged in');
  }

  async submitEndlessAnswer(
    questionId: string,
    answerIds: string[],
    answerTime: number,
    affectStreak: boolean,
    orderSeed: string
  ): Promise<{ responseId: string, evaluation: Evaluation }> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      if (affectStreak) {
        return await this.pocketbaseService.pb.send(`/response/${userId}`, {
          body: {
            question: questionId,
            given_answers: answerIds,
            endless: true,
            answer_time: answerTime,
            order_seed: orderSeed
          } as Response,
          method: 'POST',
        });
      } else {
        return await this.pocketbaseService.pb.send(`/response/${userId}`, {
          body: {
            question: questionId,
            given_answers: answerIds,
            repeat: true,
            answer_time: answerTime,
            order_seed: orderSeed
          } as Response,
          method: 'POST',
        });
      }


    }
    throw new Error('Not logged in');
  }

  async flagQuestion(questionId: string, testId?: string): Promise<string[]> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      const flagged_question: FlaggedQuestion = {
        question: questionId,
        user: userId,
        test: testId
      };

      await this.pocketbaseService.pb.collection('flagged_questions').create<FlaggedQuestion>(flagged_question);
      return this.getFlaggedQuestions(testId);
    }
    throw new Error('Not logged in');
  }

  async unflagQuestion(questionId: string, testId?: string): Promise<string[]> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      const flaggedQuestion = await this.getOneFlaggedQuestion(questionId, testId);
      if (!flaggedQuestion) {
        throw new Error('Flagged question not found');
      }

      await this.pocketbaseService.pb.collection('flagged_questions').delete(flaggedQuestion.id);
      return this.getFlaggedQuestions(testId);
    }
    throw new Error('Not logged in');
  }

  async getOneFlaggedQuestion(questionId: string, testId?: string): Promise<RecordModel | null> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      try {
        if (!testId) {
          const flaggedQuestion = await this.pocketbaseService.pb.collection('flagged_questions').getFirstListItem(`user="${userId}" && question="${questionId}"`);
          return flaggedQuestion;
        }
        const flaggedQuestion = await this.pocketbaseService.pb.collection('flagged_questions').getFirstListItem(`user="${userId}" && question="${questionId}" && test="${testId}"`);
        return flaggedQuestion;
      } catch (error) {
        return null;
      }
    }
    throw new Error('Not logged in');
  }

  async getFlaggedQuestions(testId?: string): Promise<string[]> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      if (testId) {
        const flagged_questions = (
          await this.pocketbaseService.pb
            .collection('flagged_questions')
            .getFullList<FlaggedQuestion>({
              filter: this.pocketbaseService.pb.filter('user = {:userId} && test = {:testId}', {
                userId, testId
              }),
              sort: '-created',
            })
        ).map((value) => value.question);

        return flagged_questions;
      } else {
        const flagged_questions = (
          await this.pocketbaseService.pb
            .collection('flagged_questions')
            .getFullList<FlaggedQuestion>({
              filter: this.pocketbaseService.pb.filter('user = {:userId}', {
                userId,
              }),
              sort: '-created',
            })
        ).map((value) => value.question);

        return flagged_questions;
      }
    }
    throw new Error('Not logged in');
  }

  async bookmarkQuestion(responseId: string, courseId?: string): Promise<void> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }
      await this.pocketbaseService.pb.collection('bookmarked_questions').create({
        answered_question: responseId,
        course: courseId,
        user: userId
      })
      return;
    }
    throw new Error('Not logged in');
  }

  async unBookmarkQuestion(questionId: string): Promise<void> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      const record = await this.pocketbaseService.pb.collection('bookmarked_questions').getFirstListItem(this.pocketbaseService.pb.filter('answered_question.question = {:questionId}', { questionId }));
      await this.pocketbaseService.pb.collection('bookmarked_questions').delete(record.id);
      return;
    }
    throw new Error('Not logged in');
  }

  async isQuestionBookmarked(questionId: string): Promise<boolean> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      try {
        await this.pocketbaseService.pb.collection('bookmarked_questions').getFirstListItem(`answered_question.question = "${questionId}"`);
        return true;
      }

      catch (e) {
        return false;
      }
    }
    throw new Error('Not logged in');
  }

  async getBookmarkedQuestions(): Promise<BookmarkedQuestion[]> {
    if (this.pocketbaseService.pb.authStore.model != null) {
      const userId = this.pocketbaseService.pb.authStore.model['id'];

      if (!userId) {
        throw new Error('Not logged in');
      }

      return await this.pocketbaseService.pb.collection('bookmarked_questions').getFullList<BookmarkedQuestion>({ sort: '-created', expand: 'course' });
    }
    throw new Error('Not logged in');
  }

  async submitQuestionFeedback(question: Question, feedback?: string): Promise<void> {
    await this.pocketbaseService.pb.send(`/feedback`, {
      body: {
        question,
        feedback
      },
      method: 'POST'
    });
  }

  public shuffleAnswerOptions(question: Question, seed: string): Question {
    const chance = new Chance(seed);

    const answerOptions = question.expand.answer_options;
    if (!answerOptions) {
      // no answer options, cant shuffle (shouldnt happen)
      return question;
    }

    const shuffled = chance.shuffle(answerOptions);

    // in case any answerOptions has isLast = true, needs to be at the end
    const lastQuestionIndex = shuffled.findIndex(q => q.isLast);
    if (lastQuestionIndex >= 0) {
      const lastQuestion = shuffled.splice(lastQuestionIndex, 1)[0];
      shuffled.push(lastQuestion);
    }

    question.expand.answer_options = shuffled;
    return question;
  }
}
